import { Injectable } from "@angular/core";

import { Observable } from "rxjs";
import { map, switchMap } from "rxjs/operators";

import { ValgtForbrugerObserver } from "../bruger";
import { AppserverUnauthenticatedCredentials } from "../credentials";
import { InstallationHttpClient } from "../http";
import { AktuelBrugersForbrugerTilknytningerObserver } from "./aktuel-brugers-forbruger-tilknytninger.observer";
import { ForbrugerTilknytningRepository } from "./forbruger-tilknytning.repository";
import { ForbrugerTilknytning } from "./model/forbruger-tilknytning.model";

@Injectable({ providedIn: "root" })
export class ForbrugerTilknytningService {
    constructor(
        private readonly installationHttpClient: InstallationHttpClient,
        private readonly valgtForbrugerObserver: ValgtForbrugerObserver,
        private readonly forbrugerTilknytningRepository: ForbrugerTilknytningRepository,
        private readonly aktuelBrugersForbrugerTilknytningerObserver: AktuelBrugersForbrugerTilknytningerObserver
    ) {}

    public hentTilknytningerForAktuelBruger(): Observable<ForbrugerTilknytning[]> {
        return this.aktuelBrugersForbrugerTilknytningerObserver.valueChanges;
    }

    public hentTilknytningerForValgtForbruger(): Observable<ForbrugerTilknytning[]> {
        const brugerId$ = this.valgtForbrugerObserver.valueChanges.pipe(map(b => (b && b.id) || null));
        return brugerId$.pipe(
            switchMap(brugerId => this.forbrugerTilknytningRepository.hentTilknytninger(brugerId, this.installationHttpClient))
        );
    }

    public async opretTilknytning(forsyningId: string, brugernavn: string, kodeord: string): Promise<void> {
        await this.forbrugerTilknytningRepository.opretTilknytning(forsyningId, brugernavn, kodeord);
        this.aktuelBrugersForbrugerTilknytningerObserver.triggerChanged();
    }

    public async sletTilknytning(tilknytningId: number): Promise<void> {
        await this.forbrugerTilknytningRepository.sletTilknytning(tilknytningId);
        this.aktuelBrugersForbrugerTilknytningerObserver.triggerChanged();
    }

    public async retryEksternTilknytningAuth(noegle: AppserverUnauthenticatedCredentials): Promise<boolean> {
        const ok = await this.forbrugerTilknytningRepository.retryEksternAuthenticate(noegle);
        if (ok) {
            this.aktuelBrugersForbrugerTilknytningerObserver.triggerChanged();
        }
        return ok;
    }

    /**
     * Validerer nyt kodeord mod ekstern forsyning, sletter den gamle relation og opretter tilknytning på ny
     * (slet → TilfoejEksternTilknytning), da der ikke findes et dedikeret opdater-hash-endpoint i klienten.
     */
    public async gentilknytEksternMedNytKodeord(tilknytningId: any, forsyningId: string, brugernavn: string, kodeord: string): Promise<void> {
        const ok = await this.forbrugerTilknytningRepository.validerEksternMedPlaintextKodeord(forsyningId, brugernavn, kodeord);
        if (!ok) {
            throw new Error("Ekstern login med det indtastede kodeord lykkedes ikke.");
        }
        await this.sletTilknytning(tilknytningId);
        await this.opretTilknytning(forsyningId, brugernavn, kodeord);
    }
}
